import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '../i18n';
import { api } from '../api/client';
import { useAuth } from '../lib/auth';
import { validatePasswordStrength } from '../lib/password-policy';

/**
 * 重置密码页 /reset-password?token=xxx
 *
 * 邮件链接落地页。输入新密码 → 成功后自动登录（后端返回新 JWT）并跳转 Dashboard。
 * 令牌无效/过期时提示重新获取。
 */
export default function ResetPassword() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { adoptSession } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError('');
    const pwdError = validatePasswordStrength(newPassword);
    if (pwdError) {
      setError(t(pwdError));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('settings.password.errMismatch'));
      return;
    }
    setSubmitting(true);
    try {
      const session = await api.resetPassword(token, newPassword);
      await adoptSession(session.token, session.user);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h1 className="auth-title">{t('auth.resetTitle')}</h1>
          <div className="state-box state-error">{t('auth.resetInvalidLink')}</div>
          <p style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.85rem' }}>
            <Link to="/forgot-password" style={{ color: 'var(--accent)' }}>
              {t('auth.resetReRequest')}
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1 className="auth-title">{t('auth.resetTitle')}</h1>
        <p className="auth-subtitle">{t('auth.resetSub')}</p>

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label className="form-label" htmlFor="rp-new">
              {t('settings.password.new')}
            </label>
            <input
              id="rp-new"
              className="form-input"
              type="password"
              placeholder={t('settings.password.newPlaceholder')}
              value={newPassword}
              autoComplete="new-password"
              disabled={submitting}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <div className="form-hint">{t('auth.pwdHint')}</div>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="rp-confirm">
              {t('settings.password.confirm')}
            </label>
            <input
              id="rp-confirm"
              className="form-input"
              type="password"
              value={confirmPassword}
              autoComplete="new-password"
              disabled={submitting}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
          {error && <div className="state-box state-error">{error}</div>}
          <button
            className="btn btn-primary btn-lg auth-submit"
            type="submit"
            disabled={submitting || !newPassword}
          >
            {submitting ? t('auth.submitting') : t('auth.resetSubmit')}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: '1.25rem', fontSize: '0.85rem' }}>
          <Link to="/login" style={{ color: 'var(--accent)' }}>
            {t('auth.backToLogin')}
          </Link>
        </p>
      </div>
    </div>
  );
}
